import React, { useRef, useEffect, useState, useCallback } from "react";
import { Card, CardContent, Typography, Box, CircularProgress, IconButton } from "@mui/material";
import { useDropzone } from "react-dropzone";
import useUploadImage from "hooks/useUploadImage";
import { ImagePreview } from "./styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";

const ImageUploadButton = ({
  label,
  vehicleID,
  placeholder = null,
  addImage,
  removeImage,
  image,
}) => {
  const { uploadImage, response, error, loading } = useUploadImage();
  const [preview, setPreview] = useState(null);
  const inputRef = useRef(null);

  const onDrop = useCallback(
    async (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        setPreview(URL.createObjectURL(file)); // Set image preview
        await uploadImage(file, vehicleID);
      }
    },
    [uploadImage, vehicleID]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: "image/*",
  });

  useEffect(() => {
    if (response?.success) {
      const { photoToken, photoURL } = response;

      addImage({
        name: label,
        pictureID: photoToken,
        url: photoURL,
      });
    }
  }, [response, label]);

  useEffect(() => {
    if (error) {
      // Reset input field to allow re-uploading
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    }
  }, [error]);

  const handleDelete = () => {
    setPreview(null);
    if (removeImage) {
      removeImage(label);
    }
  };

  const buttonColor = () => {
    if (error) {
      return "error";
    }

    if (response && preview) {
      return "success";
    }

    return "primary";
  };

  return (
    <Card variant="outlined" sx={{ maxWidth: 300, m: 2, textAlign: "center" }}>
      <CardContent>
        <Box
          {...getRootProps()}
          sx={{
            border: "2px dashed",
            borderColor: isDragActive ? "primary.main" : "grey.400",
            borderRadius: 1,
            p: 2,
            cursor: "pointer",
            mb: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          <input {...getInputProps()} />
          <CloudUploadIcon color={buttonColor()} sx={{ fontSize: 40, mb: 1 }} />
          <Typography>{label}</Typography>
        </Box>

        {loading ? (
          <CircularProgress />
        ) : (
          <Box
            sx={{
              position: "relative",
              display: "inline-block",
            }}
          >
            <ImagePreview>
              <img
                src={preview || image?.url || placeholder}
                alt="Selected"
                style={{ maxWidth: "100%", borderRadius: "8px" }}
              />
            </ImagePreview>
            {preview || image?.url ? (
              <IconButton
                onClick={handleDelete}
                color="error"
                size="small"
                sx={{
                  position: "absolute",
                  bottom: -10,
                  right: -10,
                  backgroundColor: "black",
                  ":hover": { backgroundColor: "grey.200" },
                }}
              >
                <DeleteIcon />
              </IconButton>
            ) : null}
          </Box>
        )}
        {error && <Typography color="error.main">{error.message}</Typography>}
      </CardContent>
    </Card>
  );
};

export default ImageUploadButton;
